import React, { useEffect, useRef, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Shield, Clock, Award, Users, Wrench, Phone, MapPin, CheckCircle } from 'lucide-react';

const advantages = [
  {
    icon: Shield,
    title: "Certifié & Assuré",
    description: "Tous nos techniciens sont certifiés et nos interventions sont couvertes par une assurance complète.",
    color: "from-blue-500 to-blue-600"
  },
  {
    icon: Clock,
    title: "Disponible 24h/24",
    description: "Service d'urgence disponible 7 jours sur 7, même les weekends et jours fériés.",
    color: "from-orange-500 to-orange-600"
  },
  {
    icon: Award,
    title: "Plus de 10 ans d'expérience",
    description: "Une expertise reconnue dans le domaine de la plomberie et du chauffage en Belgique.",
    color: "from-green-500 to-green-600"
  },
  {
    icon: Users,
    title: "Équipe Professionnelle",
    description: "Des techniciens qualifiés et formés aux dernières technologies et normes en vigueur.",
    color: "from-purple-500 to-purple-600"
  },
  {
    icon: Wrench,
    title: "Équipement Moderne",
    description: "Nous utilisons des outils et équipements de pointe pour des interventions efficaces.",
    color: "from-red-500 to-red-600"
  },
  {
    icon: Phone,
    title: "Devis Gratuit",
    description: "Estimation gratuite et transparente avant toute intervention, sans engagement.",
    color: "from-teal-500 to-teal-600"
  },
  {
    icon: MapPin,
    title: "Couverture Belgique",
    description: "Nous intervenons dans toute la Belgique avec des délais d'intervention rapides.",
    color: "from-indigo-500 to-indigo-600"
  },
  {
    icon: CheckCircle,
    title: "Garantie Qualité",
    description: "Toutes nos interventions sont garanties et nous assurons un suivi post-intervention.",
    color: "from-pink-500 to-pink-600"
  }
];

function OptimizedCard({ advantage, index }) {
  const [isVisible, setIsVisible] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.2, rootMargin: '50px' }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const IconComponent = advantage.icon;

  return (
    <motion.div
      ref={cardRef}
      initial={{ opacity: 0, y: 30, scale: 0.95 }}
      animate={isVisible ? { opacity: 1, y: 0, scale: 1 } : {}}
      transition={{ 
        duration: 0.4, 
        delay: index * 0.1,
        ease: [0.22, 1, 0.36, 1]
      }}
      className="mb-6"
    >
      <div className={`bg-gradient-to-br ${advantage.color} rounded-2xl p-6 md:p-8 shadow-lg text-white relative overflow-hidden group cursor-pointer transform-gpu will-change-transform`}>
        {/* Simplified background */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white rounded-full transform translate-x-16 -translate-y-16"></div>
        </div>

        <div className="relative z-10 flex flex-col md:flex-row items-center gap-6">
          {/* Icon */}
          <div className="flex-shrink-0">
            <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center">
              <IconComponent className="w-8 h-8 text-white" />
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 text-center md:text-left">
            <h3 className="text-2xl md:text-3xl font-bold mb-3">
              {advantage.title}
            </h3>
            <p className="text-base md:text-lg leading-relaxed opacity-90">
              {advantage.description}
            </p>
          </div>

          {/* Number Badge */}
          <div className="flex-shrink-0">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              <span className="text-lg font-bold text-white">
                {String(index + 1).padStart(2, '0')}
              </span>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export function AdvantagesOptimized() {
  return (
    <section id="avantages" className="bg-gray-50 py-20">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div 
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-blue-900 mb-4">
            Pourquoi Choisir Esiteck ?
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed max-w-2xl mx-auto">
            Votre partenaire de confiance pour tous vos besoins techniques en Belgique
          </p>
        </motion.div>

        {/* Cards Grid */}
        <div className="space-y-6">
          {advantages.map((advantage, index) => (
            <OptimizedCard
              key={index}
              advantage={advantage}
              index={index}
            />
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div 
          className="text-center mt-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          viewport={{ once: true }}
        >
          <div className="bg-white rounded-xl p-6 shadow-lg max-w-xl mx-auto">
            <h3 className="text-xl font-bold text-blue-900 mb-3">
              Prêt à faire appel à nos services ?
            </h3>
            <p className="text-gray-600 mb-4">
              Contactez-nous dès maintenant pour un devis gratuit
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button 
                className="bg-blue-900 hover:bg-blue-800 text-white px-6 py-2 rounded-full font-medium transition-colors duration-200"
                onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Devis Gratuit
              </button>
              <button 
                className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 rounded-full font-medium transition-colors duration-200"
                onClick={() => window.open('tel:+32465195726')}
              >
                Appeler Maintenant
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}